import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Field, Form } from 'react-final-form';
import { FormattedMessage, injectIntl } from 'react-intl';

import {
  TextField,
  Button,
  Headline,
  Row,
  Col,
} from '@folio/stripes-components';

import { forgotFormErrorCodes } from '../../constants';
import OrganizationLogo from '../OrganizationLogo';
import AuthErrorsContainer from '../AuthErrorsContainer';
import { AltchaWidget } from '../Recek/altcha/AltchaWidget.tsx';

import formStyles from './ForgotPasswordForm.css';

class ForgotPasswordForm extends Component {
  static propTypes = {
    isValid: PropTypes.bool.isRequired,
    onSubmit: PropTypes.func.isRequired,
    errors: PropTypes.arrayOf(PropTypes.object),
    initialEmail: PropTypes.string,
    variant: PropTypes.oneOf(['completeRegistration']),
    captchaChallengePath: PropTypes.string.isRequired,
    captchaRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.object })]),
    onCaptchaPayload: PropTypes.func.isRequired,
    intl: PropTypes.shape({
      formatMessage: PropTypes.func.isRequired,
    }),
  };

  static defaultProps = {
    errors: [],
    initialEmail: '',
    variant: undefined,
    captchaRef: undefined,
  };

  render() {
    const {
      onSubmit,
      isValid,
      errors,
      initialEmail,
      variant,
      captchaChallengePath,
      captchaRef,
      onCaptchaPayload,
      intl: { formatMessage },
    } = this.props;
    const isCompleteRegistration = variant === 'completeRegistration';
    const labels = isCompleteRegistration
      ? {
        title: 'stripes-core.label.completeRegistration',
        callToAction: 'stripes-core.label.completeRegistrationCallToAction',
        submit: 'stripes-core.button.confirm',
      }
      : {
        title: 'stripes-core.label.forgotPassword',
        callToAction: 'stripes-core.label.forgotPasswordCallToAction',
        submit: 'stripes-core.button.continue',
      };

    return (
      <Form
        onSubmit={onSubmit}
        initialValues={isCompleteRegistration ? { userInput: initialEmail } : undefined}
        render={({ handleSubmit, pristine, values }) => (
          <div className={formStyles.wrap}>
            <div className={formStyles.centered}>
              <OrganizationLogo />
              <form
                className={formStyles.form}
                data-form="forgot"
                onSubmit={handleSubmit}
              >
                <Headline
                  size="xx-large"
                  tag="h1"
                  data-test-h1
                >
                  <FormattedMessage id={labels.title} />
                </Headline>
                <Headline
                  size="large"
                  tag="p"
                  weight="regular"
                  faded
                  data-test-p
                >
                  <FormattedMessage id={labels.callToAction} />
                </Headline>
                <div className={formStyles.formGroup}>
                  <Field
                    id="input-email-or-phone"
                    component={TextField}
                    name="userInput"
                    type="text"
                    marginBottom0
                    fullWidth
                    inputClass={formStyles.passForgotInput}
                    validationEnabled={false}
                    hasClearIcon={false}
                    autoCapitalize="none"
                    autoFocus
                    placeholder={formatMessage({ id: 'stripes-core.placeholder.forgotPassword' })}
                  />
                </div>
                <div className={formStyles.formGroup}>
                  <AltchaWidget
                    ref={captchaRef}
                    challengePath={captchaChallengePath}
                    onPayload={onCaptchaPayload}
                  />
                </div>
                <Button
                  buttonStyle="primary"
                  id="clickable-login"
                  name="continue-button"
                  type="submit"
                  buttonClass={formStyles.passForgotSubmitButton}
                  // A prefilled complete-registration form is pristine yet ready to submit.
                  disabled={isCompleteRegistration ? !String(values?.userInput || '').trim() : pristine}
                  fullWidth
                  marginBottom0
                  data-test-submit
                >
                  <FormattedMessage id={labels.submit} />
                </Button>
                <Row center="xs">
                  <Col xs={12}>
                    <div
                      className={formStyles.authErrorsWrapper}
                      data-test-errors
                    >
                      <AuthErrorsContainer
                        errors={!isValid
                          ? [{ code: forgotFormErrorCodes.EMAIL_INVALID }]
                          : errors
                        }
                        data-test-container
                      />
                    </div>
                  </Col>
                </Row>
              </form>
            </div>
          </div>
        )}
      />
    );
  }
}

export default injectIntl(ForgotPasswordForm);
